import React from 'react';
import { IconCalendar, IconUsers } from '@planne-software/uni/icons';
import { CustomAccordion } from '@planne-software/uni/Accordion';

import type { ProductPackage } from '@src/domains/cart';
import { useTranslate } from '@src/core/hooks';

import {
  SummaryItemCardTitle,
  SummaryItemMetaRow,
  SummaryItemMetaText,
  SummaryPackageAccordionRoot,
  SummaryPackageAccordionTitle,
  SummaryPackageCardRoot,
  SummaryPackageDetails,
  SummaryPackageItemDivider,
  SummaryItemFooter,
  SummaryItemTotal,
  MobileSummaryRow,
  MobileSummaryRowLabel,
  MobileSummaryRowMutedValue,
} from '../../styles';
import type { SummaryLineItem } from '@src/features/transactables/summary/core/summaryLineItem';
import { SummaryItemCard } from '../SummaryItemCard';
import { SummaryItemSeller } from '../SummaryItemSeller';

import { useSummaryPackageCard } from './useSummaryPackageCard';

type SummaryPackageCardProps = {
  productPackage: ProductPackage;
  items: SummaryLineItem[];
};

export const SummaryPackageCard = (props: SummaryPackageCardProps) => {
  const { i18n } = useTranslate();
  const {
    formatCents,
    productPackage,
    packageDates,
    formattedTariffs,
    totalCents,
    discountCents,
    previousCents,
    packageSeller,
    showSeller,
    items,
  } = useSummaryPackageCard(props);

  return (
    <SummaryPackageCardRoot data-testid='summary-package-card'>
      <SummaryItemCardTitle>{productPackage.name}</SummaryItemCardTitle>

      {packageDates || formattedTariffs ? (
        <SummaryItemMetaRow>
          {packageDates ? (
            <SummaryItemMetaText>
              <IconCalendar size={16} />
              {packageDates}
            </SummaryItemMetaText>
          ) : null}
          {formattedTariffs ? (
            <SummaryItemMetaText>
              <IconUsers size={16} />
              {formattedTariffs}
            </SummaryItemMetaText>
          ) : null}
        </SummaryItemMetaRow>
      ) : null}

      {showSeller && packageSeller ? (
        <SummaryItemSeller name={packageSeller.name} logoUrl={packageSeller.logoUrl} />
      ) : null}

      <SummaryItemFooter>
        {previousCents != null && previousCents !== totalCents ? (
          <MobileSummaryRow>
            <MobileSummaryRowLabel>{i18n('cart_package_previous_price')}</MobileSummaryRowLabel>
            <MobileSummaryRowLabel>{formatCents(previousCents)}</MobileSummaryRowLabel>
          </MobileSummaryRow>
        ) : null}

        {discountCents > 0 ? (
          <MobileSummaryRow>
            <MobileSummaryRowLabel>{i18n('checkout_discounts')}</MobileSummaryRowLabel>
            <MobileSummaryRowMutedValue>{`-${formatCents(discountCents)}`}</MobileSummaryRowMutedValue>
          </MobileSummaryRow>
        ) : null}

        <SummaryItemTotal>{formatCents(totalCents)}</SummaryItemTotal>
      </SummaryItemFooter>

      <SummaryPackageAccordionRoot>
        <CustomAccordion
          showBorderBottom
          title={<SummaryPackageAccordionTitle>{i18n('checkout_view_product_details')}</SummaryPackageAccordionTitle>}
          content={
            <SummaryPackageDetails>
              {items.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index > 0 ? <SummaryPackageItemDivider /> : null}
                  <SummaryItemCard item={item} compact />
                </React.Fragment>
              ))}
            </SummaryPackageDetails>
          }
        />
      </SummaryPackageAccordionRoot>
    </SummaryPackageCardRoot>
  );
};

export default SummaryPackageCard;
